import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';
import { setLandingType } from '../plans/plansSlice';
import { osName, osVersion, isDesktop, isIOS, isAndroid } from 'react-device-detect';
import { selectEventsData, setEventDataList } from '../events/eventsSlice';
import { useSplitFlow, queryParser } from '@applyft-web/utils';
import { selectUserUuid } from '../signup/signupSlice';
import MainHeadline from '../../UI/MainHeadline';
import MainDescription from '../../UI/MainDescription';
import UIButton from '../../UI/UIButton';
import FacebookPixel from 'react-facebook-pixel';
import { FB_PIXEL_ID } from '../../analytics/facebook';

const StyledImg = styled.div`
  position: relative;
  flex-grow: 1;
  max-height: 470px;

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: url(${'../assets/screens/welcome.jpg'}) center top / 100% no-repeat;
  }
`;

const BottomWrapper = styled.div`
  width: 100%;
  position: relative;
  isolation: isolate;

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 340px;
    background: linear-gradient(180.21deg, rgba(255, 255, 255, 0) 19.86%, #ffffff 44.65%);
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: -1;
  }
`;

const Welcome = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [eventParams, setEventParams] = useState(null);
  const eventsData = useSelector(selectEventsData);
  const userUuid = useSelector(selectUserUuid);
  const {
    location: { pathname },
  } = props;
  const landingParam = pathname.replace(/\//g, '');
  const { landingType, flowType: paywallType } = useSplitFlow(landingParam, {
    debug: process.env.REACT_APP_ENV !== 'production',
  });

  useEffect(() => {
    if (eventParams || !paywallType) return;

    const {
      history: {
        location: { search },
      },
    } = props;

    const { fbclid, gclid, ...utms } = queryParser(search);
    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    } = utms;
    const facebookUtm = fbclid ? 'facebook' : null;
    const appDomain = `${window.location.origin}${landingParam.length ? '/' + landingParam : ''}`;
    const utmSource = utms['utm_source'] || facebookUtm || 'organic';
    utms['utm_source'] = utmSource;

    dispatch(setLandingType(paywallType));

    const localEventsData = {
      landing_type: `pairs_${landingType}`,
      app_domain: appDomain,
      app_name: 'Familo',
      landingType,
      appDomain,
      appName: 'Familo',
      fbclid,
      gclid,
      utm: utms,
      utm_source: utmSource,
      os: `${osVersion} ${osName}`,
    };

    const userProperties = {
      AppDomain: appDomain,
      OSDevice: `${isDesktop ? 'desktop' : isIOS ? 'iOS' : isAndroid ? 'Android' : osName}`,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    };

    dispatch(setEventDataList(localEventsData));
    setAmplitudeUserProperties(userProperties);
    setEventParams(localEventsData);
  }, [props, dispatch, eventParams, userUuid, landingParam, landingType, paywallType]);

  useEffect(() => {
    if (!eventParams) return;
    sendAnalyticsEvents(EVENTS.sessionStart, eventParams);
    sendAnalyticsEvents(EVENTS.onboardingStarted, eventParams);
  }, [eventParams]);

  const onContinueClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingStartedSubmitted, eventsData);
    props.history.push('/name');
  };

  useEffect(() => {
    FacebookPixel.init(FB_PIXEL_ID, { external_id: userUuid });
    FacebookPixel.pageView();
  }, [userUuid]);

  return (
    <>
      <StyledImg />
      <BottomWrapper>
        <MainHeadline>{t('welcome')}</MainHeadline>
        <MainDescription>{t('know_were_your_loved_one_is')}</MainDescription>
        <UIButton onClick={onContinueClick} />
      </BottomWrapper>
    </>
  );
};

export default Welcome;
